import { marked } from 'marked';
import DOMPurify from 'dompurify';

// Helper function for HTML escaping
const escapeHtml = (s = "") => String(s).replace(/[&<>"']/g, c => ({
	'&': '&amp;',
	'<': '&lt;',
	'>': '&gt;',
	'"': '&quot;',
	"'": '&#39;'
}[c]));

// Replace tokens like [zpid_12345678] (optionally wrapped in parentheses) with street addresses.
// Options:
//   - clickable (default: true): when false, renders plain text (or a <span>) instead of an <A> tag.
//   - underline (default: true): when false, removes the underline decoration.
//   - className (default: "colour-primary"): extra class to add to the clickable link or span wrapper.
const replaceZpidTokens = (text, allComps = [], opts = {}) => {
	// Normalize to a string so downstream callers (marked) never receive arrays/objects
	if (typeof text !== "string") {
		if (Array.isArray(text)) {
			text = text.map(x => (x == null ? "" : typeof x === "string" ? x : (typeof x === "object" ? JSON.stringify(x) : String(x)))).join("\n");
		} else if (text == null) {
			text = "";
		} else if (typeof text === "object") {
			// Try a friendly toString; fall back to JSON
			text = typeof text.toString === "function" && text.toString !== Object.prototype.toString
				? String(text)
				: JSON.stringify(text);
		} else {
			text = String(text);
		}
	}
	if (!Array.isArray(allComps)) allComps = [];

	// Clean up empty brackets [] that LLM might have generated (despite instructions not to)
	text = text.replace(/\[\s*\]/g, '');

	const { clickable = true, underline = true, className = "colour-primary", clickedZPID = null } = opts;

	// Build a lookup map for O(1) resolution by ZPID
	const zpidToAddress = new Map();
	for (let i = 0; i < allComps.length; i++) {
		const comp = allComps[i];
		// Check both comp.zpid and comp.uniqueIds.zillowId (comps can have either structure)
		const zpid = comp?.zpid || comp?.uniqueIds?.zillowId;
		const addr = comp?.addressDetails?.streetAddress;
		if (zpid != null && addr) {
			zpidToAddress.set(String(zpid), addr);
		}
	}

	// Match either "([zpid_123])" with optional whitespace OR bare "[zpid_123]"
	const tokenRe = /\(\s*\[zpid_(\d+)\]\s*\)|\[zpid_(\d+)\]/gi;

	return text.replace(tokenRe, (match, g1, g2) => {
		const zpid = String(g1 || g2);
		const address = zpidToAddress.get(zpid);
		if (!address) return match; // leave untouched if not found

		// Check if this zpid matches the clickedZPID
		const isSelected = clickedZPID !== null && String(clickedZPID) === zpid;
		const boldClass = isSelected ? "body-bold" : "";

		// If not clickable, emit plain text only (no underline, no classes) to preserve surrounding styles
		if (!clickable) {
			return isSelected ? `<strong>${escapeHtml(address)}</strong>` : escapeHtml(address);
		}

		const inner = underline ? `<u>${escapeHtml(address)}</u>` : escapeHtml(address);

		// Otherwise, output a clickable anchor with a stable hook for delegated onClick
		return `<a href="#" class="zpid-address ${className} ${boldClass}" data-zpid="${zpid}">${inner}</a>`;
	});
};

export const mergeAdjustmentAbstracts = (content) => {
	if (!content) return "";

	// If a JSON string was passed, try to parse it to an array
	if (typeof content === "string") {
		try {
			const parsed = JSON.parse(content);
			if (Array.isArray(parsed)) content = parsed;
		} catch (_) {
			// leave as-is if not JSON
		}
	}

	// Normalize to an array of items
	const items = Array.isArray(content) ? content : [content];

	// Pull `abstract` from objects (or use the string directly), trim, and join
	const merged = items
		.map((it) => {
			if (!it) return "";
			if (typeof it === "string") return it;
			if (typeof it.abstract === "string") return it.abstract;
			return "";
		})
		.map((s) => s.trim())
		.filter(Boolean)
		// Use HTML line breaks so Typewriter/renderHTML can display spacing
		.join();

	return merged;
};

export const renderHTML = (inputText, allComps = [], opts = {}) => {
	// 1) Normalize to a string early (arrays/objects occasionally sneak in from GPT responses)
	let normalized = "";
	if (typeof inputText === "string") {
		normalized = inputText;
	} else if (Array.isArray(inputText)) {
		// If these are adjustment objects, merge their abstracts; otherwise join sensibly
		try {
			normalized = mergeAdjustmentAbstracts(inputText);
			if (typeof normalized !== "string" || normalized.length === 0) {
				normalized = inputText.map(x => (x == null ? "" : typeof x === "string" ? x : (typeof x === "object" ? JSON.stringify(x) : String(x)))).join("\n");
			}
		} catch (_) {
			normalized = inputText.map(x => (x == null ? "" : typeof x === "string" ? x : (typeof x === "object" ? JSON.stringify(x) : String(x)))).join("\n");
		}
	} else if (inputText == null) {
		normalized = "";
	} else if (typeof inputText === "object") {
		normalized = typeof inputText.toString === "function" && inputText.toString !== Object.prototype.toString
			? String(inputText)
			: JSON.stringify(inputText);
	} else {
		normalized = String(inputText);
	}

	// 2) Resolve any [zpid_########] tokens to street addresses **before** parsing Markdown
	const textWithAddresses = replaceZpidTokens(normalized, allComps, opts);

	// 3) Ensure the custom ~~strike~~ tokenizer is configured before parsing
	marked.use({
		tokenizer: {
			del(src) {
				const cap = /^~~(?=\S)([\s\S]*?\S)~~/.exec(src);
				if (cap) {
					return {
						type: 'del',
						raw:  cap[0],
						text: cap[1],
						tokens: this.lexer.inlineTokens(cap[1])
					};
				}
			}
		},
			renderer: {
				link(...args) {
					// Support both signatures:
					//  - Old: link(href, title, text)
					//  - New: link(token)
					let href, title, text;
					if (args.length === 1 && typeof args[0] === 'object' && args[0] !== null) {
						const token = args[0];
						href = token.href;
						title = token.title;
						text = token.text;
					} else {
						[href, title, text] = args;
					}


					const hasHref = typeof href === 'string' && href.length > 0;
					if (!hasHref) {
						// No valid href: just return the visible text without wrapping in an anchor
						return typeof text === 'string' && text.length > 0 ? text : '';
					}
					const safeHref = href;
					const titleAttr = title ? ` title="${title}"` : '';
					const linkText = (typeof text === 'string' && text.length > 0) ? text : safeHref;
					return `<a href="${safeHref}"${titleAttr} target="_blank" rel="noopener noreferrer">${linkText}</a>`;
				}
			}
	});

	// 4) Safely convert Markdown to HTML and sanitize
	const rawHTML = marked(textWithAddresses);
	return DOMPurify.sanitize(rawHTML);
};

export const renderHTMLAdjustments = (inputText, allComps = [], opts = {}) => {
	// Expect an array of items shaped like { abstract, adjustments, zpid }
	// but gracefully handle a single object too
	const items = Array.isArray(inputText) ? inputText : [inputText];

	// Configure marked BEFORE any parse to preserve ~~strike~~ behavior
	marked.use({
		tokenizer: {
			del(src) {
				const cap = /^~~(?=\S)([\s\S]*?\S)~~/.exec(src);
				if (cap) {
					return {
						type: 'del',
						raw:  cap[0],
						text: cap[1],
						tokens: this.lexer.inlineTokens(cap[1])
					};
				}
			}
		}
	});

	// Build per‑item HTML blocks. We pre-render markdown so that the abstract
	// still becomes a <p> even when wrapped in a <div> container.
	const blocks = items.map((it, idx) => {
		const zpid = String(it?.zpid ?? idx);

		// 1) Abstract with [zpid_########] → clickable address, then parse as inline MD
		const abstractRaw = typeof it?.abstract === "string" ? it.abstract : "";
		const abstractWithAddresses = replaceZpidTokens(abstractRaw, allComps, opts);
		const abstractHTML = `<p>${marked.parseInline(abstractWithAddresses)}</p>`;

		// 2) Normalize adjustments into a Markdown bullet list, then render to HTML
		const adjustmentsRaw = typeof it?.adjustments === "string" ? it.adjustments.trim() : "";
		let adjustmentsMd = adjustmentsRaw;
		if (adjustmentsRaw) {
			adjustmentsMd = adjustmentsRaw
				.replace(/^\s*-\s*/, "- ")
				.replace(/\s+-\s+/g, "\n- ");
		}
		let adjustmentsHTML = adjustmentsMd ? marked(adjustmentsMd) : "";
		
		// Add Redfin logo after "market trend adjustment" if zipcode is available
		// Do this after markdown rendering to avoid DOMPurify issues
		const zipcode = opts?.zipcode;
		if (zipcode && adjustmentsHTML) {
			const redfinLogoLink = `<a href="https://www.redfin.com/zipcode/${escapeHtml(zipcode)}/housing-market" target="_blank" rel="noopener noreferrer" class="redfin-logo-link" style="display: inline-block; vertical-align: middle; margin-left: 4px; text-decoration: none; line-height: 1;"><svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 12 16" fill="none" style="display: inline-block; vertical-align: middle;"><path d="M11.8171 15.1981C11.5772 15.101 11.3366 14.9157 10.6912 13.8182L9.40501 11.6646L7.88899 9.1263L7.76044 8.91045C7.83463 8.8894 7.90743 8.86511 7.97958 8.84087C9.71983 8.23817 10.7357 6.62935 10.7357 4.33367C10.7357 3.19924 10.2814 2.15151 9.41399 1.3675C9.28753 1.259 9.1569 1.15553 9.01713 1.06102C7.85916 0.289584 6.29968 0.00178123 4.22239 0.00178123L0.293939 0H0.292068V0.000863627H0.234236C-0.021143 -0.00221304 -0.0452485 0.146061 0.0549149 0.280516C0.145351 0.399966 0.247599 0.559683 0.292068 0.965317V13.63C0.247812 14.1458 0.143961 14.3101 0.0549149 14.4288C-0.0463174 14.5629 -0.00158065 14.7164 0.253745 14.7132H3.16265V9.34129H4.48471L5.4135 10.9346L6.58146 12.9391L7.1492 13.9135C7.8706 15.1397 8.50189 15.8193 9.75858 15.9644C9.93004 15.987 10.1129 15.9981 10.31 16C10.3254 16 10.4893 16 10.5045 16C10.9564 16 11.3976 15.8168 11.6723 15.6989C11.7097 15.6849 11.746 15.6703 11.7821 15.6541C12.1117 15.5065 12.0204 15.2799 11.8171 15.1981ZM5.01065 7.28608H3.26014H3.16255V2.10406H5.01065C5.92543 2.10406 6.62294 2.22383 7.14535 2.70309C7.33836 2.88769 7.48759 3.11072 7.59336 3.37996C7.72158 3.70635 7.78615 4.09909 7.78615 4.5723C7.7862 5.26072 7.72362 7.28608 5.01065 7.28608Z" fill="#A02021"/></svg></a>`;
			// Match "market trend adjustment" (case insensitive) in the rendered HTML and add Redfin logo after it
			adjustmentsHTML = adjustmentsHTML.replace(
				/(market trend adjustment)/gi,
				`$1 ${redfinLogoLink}`
			);
		}

		// 3) Wrap the status, abstract <p> and the <details> in a single container div
		return (
			`<div class="small-chat-adjustments-element" data-zpid="${zpid}">` +
				`${abstractHTML}` +
				`<details class="clz-adjustments" data-zpid="${zpid}">` +
					`<summary class="cursor-pointer">Adjustments</summary>` +
					`${adjustmentsHTML}` +
				`</details>` +
			`</div>`
		);
	});

	// We already produced HTML for each block; just join and sanitize.
	const rawHTML = blocks.join("\n\n");
	return DOMPurify.sanitize(rawHTML, { 
		ADD_TAGS: ['details', 'summary', 'svg', 'path'],
		ADD_ATTR: ['xmlns', 'viewBox', 'fill', 'width', 'height', 'style', 'target', 'rel', 'href', 'class']
	});
};

// Attach a delegated click handler for any generated address links
export const wireZpidClicks = (rootEl, arg2, arg3) => {
	// Usage options:
	// 1) wireZpidClicks(rootEl, ({ zpid, address, el, event }) => { ... })
	// 2) wireZpidClicks(rootEl, allCompsArray, viewCompFn)  // will call viewCompFn(compObj)

	if (!rootEl || typeof rootEl.addEventListener !== 'function') return;
	rootEl.addEventListener('click', (e) => {
		const el = e.target.closest?.('.zpid-address');
		if (!el) return;
		e.preventDefault();
		const zpid = el.getAttribute('data-zpid');
		const address = el.textContent;

		// Mode 2: (rootEl, allComps, viewComp)
		if (Array.isArray(arg2) && typeof arg3 === 'function') {
			const allComps = arg2;
			const viewComp = arg3;
			const idx = allComps.findIndex(c => String(c?.uniqueIds?.zillowId) === String(zpid));
			if (idx !== -1) {
				try { viewComp(allComps[idx]); } catch(_) {}
			}
			return;
		}

		// Mode 1: (rootEl, onClick)
		const onClick = arg2;
		try { onClick && onClick({ zpid, address, el, event: e }); } catch(_) {}
	});
};
